import { Component, inject, OnInit, signal } from '@angular/core';
import { ContractService } from '../../services/contract.service';
import { ViewTransactionService } from '../../services/view-transaction.service';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { AppkitService } from '../../services/appkit.service';
import { environment } from '../../../../environments/environment';
import { NgClass } from '@angular/common';
import { wagmiConfig } from '../../config/wagmi-config';
import { waitForTransactionReceipt } from '@wagmi/core';
import { SocketService } from '../../services/socket.service';

@Component({
  selector: 'app-view-all-transaction',
  imports: [NgClass],
  templateUrl: './view-all-transaction.component.html',
  styleUrl: './view-all-transaction.component.scss'
})
export class ViewAllTransactionComponent implements OnInit {

  contractService = inject(ContractService);
  viewTransactionService = inject(ViewTransactionService);
  toastr = inject(ToastrService);
  appkitService = inject(AppkitService);
  transactionList: any[] = [];
  isProcessing = signal(false);
  isApproving = signal(false);
  connectedAddress: string = '';
  currentPage = 1;
  limit = 20;
  totalPages = 1;
  approvingSignals = new Map<number, ReturnType<typeof signal>>();
  contractAddress: any;
  expandedTransactions = new Set<number>();
  socketService = inject(SocketService);

  ngOnInit(): void {
    this.appkitService.initializeAppkit();
    this.contractAddress = JSON.parse(localStorage.getItem('loginResponse') || '');
    this.connectedAddress = this.contractAddress.walletAddress;
    this.viewAllTransactions(1);
    this.listenProposalEvent();
    this.listenApprovedEvent();
  }

  /**
   * Views all transactions
   * @param page
   */
  viewAllTransactions(page: number = 1) {
    this.isProcessing.set(true);
    this.viewTransactionService.getAllTransactions('TransactionProposed', page, this.limit).subscribe({
      next: (response: any) => {
        this.transactionList = response.data.transactions;
        this.currentPage = response.data.pagination.page;
        this.totalPages = response.data.pagination.pages;
        this.limit = response.data.pagination.limit;
        this.isProcessing.set(false);
      },
      error: (error: HttpErrorResponse) => {
        console.log('transaction err', error);
        this.isProcessing.set(false);
      }
    })
  }

  /**
   * Determines whether page change on
   * @param page
   */
  onPageChange(page: number) {
    if (page !== this.currentPage && page > 0 && page <= this.totalPages) {
      this.viewAllTransactions(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Approves reject transaction
   * @param transaction - Transaction object containing tx_id_numeric, contract_address, and network
   */
  async approveTransaction(transaction: any) {
    const txId = transaction.tx_id_numeric;
    const contractAddress = transaction.contract_address;
    const network = transaction.network ?? 'BSC';
    console.log('network', network);
    
    if (!contractAddress) {
      this.toastr.error('Contract address not found for this transaction');
      return;
    }

    if (!this.approvingSignals.has(txId)) {
      this.approvingSignals.set(txId, signal(false));
    }
  
    const txSignal = this.approvingSignals.get(txId)!;
    if (txSignal()) return; // already approving
  
    txSignal.set(true);
    try {
      // Ensure we're on the correct network before approving (delegated to AppkitService)
      const networkSwitched = await this.appkitService.ensureNetworkForTransaction(network);
      if (!networkSwitched) {
        txSignal.set(false);
        return;
      }

      await this.contractService.approveTx(contractAddress, txId).then(async(response: any) => {
        if (response) {
          const receipt = await waitForTransactionReceipt(wagmiConfig,{
            confirmations: 1,
            hash: response as `0x${string}`, // Type assertion to ensure correct format
          });
          console.log('approve receipt', receipt);
          
          if (receipt) {
            this.toastr.success('Transaction Approved Successfully');
      
            // Update local transaction to show approval instantly
            const txIndex = this.transactionList.findIndex(t => t.tx_id_numeric === txId);
            if (txIndex !== -1) {
              // Update approvalCount
              this.transactionList[txIndex].approvalCount += 1;
              // Add current user to approvals array
              this.transactionList[txIndex].approvals = this.transactionList[txIndex].approvals || [];
              this.transactionList[txIndex].approvals.push({ signer: this.connectedAddress });
            }
          }
        }
      })
    } catch (error) {
      console.error(error);
      this.toastr.error('Failed to approve transaction');
    } finally {
      txSignal.set(false);
    }
  }

  isApproved(transaction: any): boolean {
    // Check if transaction has enough approvals based on its required_signatures
    return transaction.approvalCount >= (transaction.required_signatures || 0);
  }
  

  /**
   * Determines whether approve disabled is
   * @param transaction
   * @param connectedAddress
   * @returns true if approve disabled
   */
  isApproveDisabled(transaction: any, connectedAddress: string): boolean {
    // 1. If transaction is already fully approved
    const requiredSignatures = transaction.required_signatures || 0;
    if (transaction.approvalCount >= requiredSignatures) {
      return true;
    }
  
    // 2. If current user already approved this transaction
    if (transaction.approvals?.some((a: any) => a.signer.toLowerCase() === connectedAddress?.toLowerCase())) {
      return true;
    }
  
    // 3. If transaction status is not "pending" (e.g., completed, expired)
    if (transaction.status !== 'pending') {
      return true;
    }
  
    // 4. If transaction expired
    if (this.isTransactionExpired(transaction)) {
      return true;
    }
    return false;
  }

  /**
   * Gets transaction status
   * @param transaction
   * @returns transaction status
   */
  getTransactionStatus(transaction: any): string {
    const isExpired = this.isTransactionExpired(transaction);
    const requiredSignatures = transaction.required_signatures || 0;
    if (transaction.approvalCount >= requiredSignatures) {
      return 'Completed';
    } else {
      return isExpired ? 'Expired' : 'Pending';
    }
  }

  /**
   * Determines whether transaction expired is
   * @param transaction
   * @returns true if transaction expired
   */
  isTransactionExpired(transaction: any): boolean {
    const now = Math.floor(Date.now() / 1000);
    return transaction.tx_expires_at && now > Number(transaction.tx_expires_at);
  }
  

  /**
   * Gets formatted date time
   * @param timestamp
   * @returns formatted date time
   */
  getFormattedDateTime(timestamp: number): string {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp) * 1000); // Convert seconds to ms
    return date.toLocaleString('en-US', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  /**
   * Toggles details
   * @param txId
   */
  toggleDetails(txId: number) {
    if (this.expandedTransactions.has(txId)) {
      this.expandedTransactions.delete(txId);
    } else {
      this.expandedTransactions.add(txId);
    }
  }


  listenProposalEvent() {
    this.socketService.on('transaction_proposed').subscribe((data: any) => {
      console.log('socket proposal data', data);
      this.viewAllTransactions(1);
    })
  }

  listenApprovedEvent() {
    this.socketService.on('transaction_approved').subscribe((data: any) => {
      console.log('socket proposal data', data);
      this.viewAllTransactions(1);
    })
  }

  /**
   * Gets explorer URL for a transaction based on its network
   * @param transaction
   * @returns explorer URL
   */
  getExplorerUrl(transaction: any): string {
    if (!transaction || !transaction.network) {
      return environment.EXPLORER_BASE_URL;
    }

    const network = (transaction.network ?? 'BSC').toUpperCase();
    const explorerUrls = (environment as any).EXPLORER_URLS || {};
    
    // Try exact match - EXPLORER_URLS contains mappings like 'BSC', 'ETH', 'POLYGON'
    if (explorerUrls[network]) {
      return explorerUrls[network];
    }

    // Fallback to default explorer URL if network not found
    return environment.EXPLORER_BASE_URL;
  }
}


